import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Trophy, Star, Medal, Target, ChevronRight, Sparkles } from 'lucide-react';
import { Progress } from '../ui/progress';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  type: 'milestone' | 'assessment' | 'learning' | 'career';
  coins: number;
  requirements?: string[];
  isLocked?: boolean;
}

interface EnhancedAchievementsProps {
  progress: {
    achievements?: string[];
    total_experience?: number;
    level?: number;
  } | null;
}

export function EnhancedAchievements({ progress }: EnhancedAchievementsProps) {
  const navigate = useNavigate();
  const [hoveredAchievement, setHoveredAchievement] = useState<string | null>(null);

  const achievements: Achievement[] = [
    {
      id: 'ONBOARDING_COMPLETE',
      title: 'First Steps',
      description: 'Complete the onboarding process',
      icon: <Star className="h-5 w-5 text-yellow-500" />,
      type: 'milestone',
      coins: 100
    },
    {
      id: 'SKILLS_ASSESSMENT',
      title: 'Skills Pioneer',
      description: 'Complete your first skills assessment',
      icon: <Target className="h-5 w-5 text-blue-500" />,
      type: 'assessment',
      coins: 150
    },
    {
      id: 'FIRST_PATH_COMPLETE',
      title: 'Path Finder',
      description: 'Complete your first learning path',
      icon: <Medal className="h-5 w-5 text-purple-500" />,
      type: 'learning',
      coins: 200
    },
    {
      id: 'CAREER_GOALS_SET',
      title: 'Goal Setter',
      description: 'Define your career goals',
      icon: <Trophy className="h-5 w-5 text-green-500" />,
      type: 'career',
      coins: 175
    }
  ];

  const userAchievements = progress?.achievements || [];
  const completedCount = userAchievements.length;
  const totalCount = achievements.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Achievements</h3>
        <Badge variant="outline">
          {completedCount}/{totalCount}
        </Badge>
      </div>

      <Progress value={(completedCount / totalCount) * 100} className="h-2" />

      <AnimatePresence>
        {achievements.map((achievement) => {
          const isCompleted = userAchievements.includes(achievement.id);
          
          return (
            <motion.div
              key={achievement.id}
              whileHover={{ scale: 1.02 }}
              onHoverStart={() => setHoveredAchievement(achievement.id)}
              onHoverEnd={() => setHoveredAchievement(null)}
              className={`flex items-start space-x-3 p-4 rounded-lg transition-all cursor-pointer ${
                isCompleted ? 'bg-gradient-to-r from-primary-50 to-primary-100' : 'bg-gray-50'
              }`}
            >
              <div className={`p-3 rounded-full ${
                isCompleted ? 'bg-primary-200' : 'bg-gray-200'
              }`}>
                {achievement.icon}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">
                    {achievement.title}
                  </h4>
                  {isCompleted ? (
                    <Badge variant="success" className="flex items-center gap-1">
                      <span>+{achievement.coins}</span>
                      <Sparkles className="h-3 w-3" />
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">
                      Locked
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {achievement.description}
                </p>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
} 